import PropTypes from 'prop-types';
import {
    Suspense, useCallback, useEffect, useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAddToCartGtmEvent } from 'Component/AddToCart/hooks/useAddToCartGtmEvent';
import { ADDED_PRODUCT_POPUP_ID } from 'Component/AddToCartPopUp/AddToCartPopUp.config';
import { ADD_TO_CART_BUTTON_ID } from 'Component/App/App.config';
import Button from 'Component/Button';
import { BUTTON_SKELETON,WIDTH_FULL } from 'Component/Button/Button.config';
import { PAGE_TYPE_CART } from 'Component/Meta/Meta.config';
import SkeletonComponent from 'Component/Skeleton/Skeleton.component';
import { event as gtmEvent } from 'Store/GoogleTagManager/GoogleTagManager.action';
import { showNotification } from 'Store/Notification/Notification.action';
import { hideActiveOverlay } from 'Store/Overlay/Overlay.action';
import { closePopup } from 'Store/Popup/Popup.action';
import { ProductType } from 'Type/ProductList.type';
import { EVENT_CHORD_PRODUCT_ADD_TO_CART } from 'Util/ChordAnalytics/ChordAnalytics.bridgeEvents';
import { lazyComponentLoader } from 'Util/lazyComponentLoader';
import { ADD_TO_CART } from 'Util/Product';
import {
    getMaxQuantity,
    getMinQuantity,
    getName,
    getProductInStock
} from 'Util/Product/Extract';
import PRODUCT_TYPE from 'Util/Product/Product.config';
import { getLineAttributeEnteredOptions, magentoProductTransform } from 'Util/Product/Transform';

export const AddToCartPopUpComponent = lazyComponentLoader(() => import(
    /* webpackMode: "lazy", webpackChunkName: "product-additional" */
    'Component/AddToCartPopUp'
), 2);

/** @namespace Bodypwa/Component/AddToCart/Component */
export default function AddToCart({
    selectedProduct,
    product,
    quantity = 1,
    isDisabled = false,
    addToCart = null,
    isRedirect = false,
    designType = 'primary',
    size = 'large',
    activePlacementLabel = null,
    lineAttributes = {}
}) {
    const dispatch = useDispatch();

    const cartId = useSelector((state) => state.CartReducer.id);
    const pageType = useSelector((state) => state.UrlRewritesReducer.pageType);
    const [isAdding, setIsAdding] = useState(false);
    const [isOneMoreAdded, setIsOneMoreAdded] = useState(false);

    const showInfo = useCallback(
        (message) => dispatch(showNotification('info', message)),
        [dispatch]
    );

    const sendAddToCartGtmEvent = useAddToCartGtmEvent();

    const sendGtmEvent = useCallback(() => {
        sendAddToCartGtmEvent(product, selectedProduct, quantity);
        // sub-object doesn't carry on its own.
        const { variants } = product;

        const configurableVariantIndex = variants
            ? variants.findIndex((variant) => variant.sku === selectedProduct?.sku)
            : false;

        const productToAdd = variants
            ? {
                ...selectedProduct,
                type_id: product.type_id,
                categories: product.categories,
                attributes: product.attributes,
                variants: product.variants,
                configurable_options: product.configurable_options,
                price_range: selectedProduct?.price_range || product.price_range,
                url: selectedProduct?.url || product.url,
                short_description: selectedProduct?.short_description || product.short_description,
                meta_description: selectedProduct?.meta_description || product.meta_description
            }
            : selectedProduct;

        dispatch(gtmEvent(
            EVENT_CHORD_PRODUCT_ADD_TO_CART,
            { product: productToAdd, quantity, configurableVariantIndex }
        ));
    }, [sendAddToCartGtmEvent, dispatch, selectedProduct, quantity, product]);

    const fallbackAddToCart = useCallback(async (options) => {
        const { default: dispatcher } = await import('Store/Cart/Cart.dispatcher');
        return dispatcher.addProductToCart(dispatch, options);
    }, [dispatch]);

    const validateQuantity = useCallback(() => {
        const minQty = getMinQuantity(selectedProduct);
        const maxQty = getMaxQuantity(selectedProduct);
        const inRange = quantity >= minQty && quantity <= maxQty;
        const typeId = selectedProduct?.type_id;

        const isValid = typeId === PRODUCT_TYPE.grouped || inRange;

        if (!isValid) {
            if (quantity < minQty) {
                showInfo(__('Sorry! Minimum quantity for this product is %s!', minQty));
            } else {
                showInfo(__('Sorry! Maximum quantity for this product is %s!', maxQty));
            }
        }

        return isValid;
    }, [selectedProduct, quantity, showInfo]);

    const validateStock = useCallback(() => {
        const inStock = getProductInStock(selectedProduct);

        if (!inStock) {
            const name = getName(selectedProduct);
            showInfo(__('Sorry! The product %s is out of stock!', name));
        }

        return inStock;
    }, [selectedProduct, showInfo]);

    const validateConfigurable = useCallback(() => {
        if (!selectedProduct) {
            showInfo(__('Please select product options.'));
            return false;
        }

        return true;
    }, [selectedProduct, showInfo]);

    useEffect(() => () => {
        dispatch(closePopup(ADDED_PRODUCT_POPUP_ID, {}));
        dispatch(hideActiveOverlay());
    },
    []);

    const validateByType = useCallback(() => {
        const typeId = selectedProduct?.type_id;
        if (typeId !== PRODUCT_TYPE.configurable) {
            return true;
        }

        return validateConfigurable();
    }, [selectedProduct, validateConfigurable]);

    const validate = useCallback(() => (
        validateStock()
        && validateQuantity()
        && validateByType()
    ), [validateStock, validateQuantity, validateByType]);

    const addProductToCartHandler = useCallback(async (e, isPopup = true) => {
        e.preventDefault();
        const products = magentoProductTransform(
            ADD_TO_CART,
            product,
            quantity,
            getLineAttributeEnteredOptions(lineAttributes),
            []
        );

        const effectiveAddToCart = addToCart || (() => fallbackAddToCart({
            products,
            product,
            quantity,
            cartId,
            isCartPage: pageType === PAGE_TYPE_CART,
            showPopUp: isPopup
        }));

        setIsAdding(true);

        if (!validate()) {
            setIsAdding(false);
            return;
        }

        try {
            await effectiveAddToCart(isPopup);
            if (isPopup) {
                if (isOneMoreAdded) {
                    setIsOneMoreAdded(false);
                }
            } else {
                setIsOneMoreAdded(true);
            }
            if (!isRedirect) {
                sendGtmEvent();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsAdding(false);
        }
    }, [
        addToCart,
        fallbackAddToCart,
        validate,
        quantity,
        cartId,
        isOneMoreAdded,
        sendGtmEvent,
        isRedirect,
        pageType,
        product,
        lineAttributes
    ]);

    if (isDisabled) {
        return <SkeletonComponent items={ BUTTON_SKELETON } />;
    }

    return (
        <>
            <Suspense fallback={ null }>
                <AddToCartPopUpComponent
                    quantity={ quantity }
                    // eslint-disable-next-line react/jsx-no-bind
                    addOneMore={ (e) => addProductToCartHandler(e, false) }
                    isOneMoreAdded={ isOneMoreAdded }
                    isAdding={ isAdding }
                    product={ product }
                    selectedProduct={ selectedProduct }
                    activePlacementLabel={ activePlacementLabel }
                />
            </Suspense>
            <Button
                onClick={ addProductToCartHandler }
                isDisabled={ isDisabled }
                isLoading={ isAdding }
                width={ WIDTH_FULL }
                id={ ADD_TO_CART_BUTTON_ID }
                type={ designType }
                size={ size }
                textSize={ size }
                text={ __('Add to cart') }
            />
        </>
    );
}

AddToCart.propTypes = {
    selectedProduct: ProductType.isRequired,
    quantity: PropTypes.number,
    isDisabled: PropTypes.bool,
    addToCart: PropTypes.func,
    product: ProductType.isRequired,
    isRedirect: PropTypes.bool,
    designType: PropTypes.string,
    size: PropTypes.string,
    activePlacementLabel: PropTypes.string,
    lineAttributes: PropTypes.objectOf(PropTypes.string)
};
AddToCart.defaultProps = {
    quantity: 1,
    isDisabled: false,
    addToCart: null,
    isRedirect: false,
    designType: 'primary',
    size: 'large',
    lineAttributes: {}
};